// import React, { useState } from 'react';
// import { Box, ThemeProvider } from '@mui/material';
// import Sidebar from './components/Sidebar';
// import Navbar from './components/Navbar';
// import AppRoutes from './routes/AppRoutes';
// import theme from './config/theme';

// function App() {
//   const [sidebarOpen, setSidebarOpen] = useState(true);

//   return (
//     <ThemeProvider theme={theme}>
//       <Box sx={{ display: 'flex', height: '100vh' }}>
//         {/* Sidebar */}
//         <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

//         {/* Right Section - Navbar at top + Main Content */}
//         <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}>
//           {/* Navbar */}
//           <Navbar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

//           {/* Main Content Area */}
//           <Box
//             sx={{
//               flex: 1,
//               padding: '20px',
//               overflowY: 'auto',
//               backgroundColor: 'background.default',
//             }}
//           >
//             <AppRoutes />
//           </Box>
//         </Box>
//       </Box>
//     </ThemeProvider>
//   );
// }

// export default App;


import React, { useState, useEffect } from 'react';
import { Provider } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider, CssBaseline, Box, useMediaQuery } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import Navbar from './components/Navbar';
import theme from './config/theme';
import Dashboard from './components/Dashboard';
import AppRoutes from './routes/AppRoutes';
import ErrorModal from './components/common/modal/ErrorModal';
import GlobalLoader from './components/common/GlobalLoader';

import { store } from './store';
import { setCredentials, logout, updateUser } from './store/authSlice';
import { useGetCurrentUserQuery } from './services/authApi';
import { clearGlobalError } from './store/uiSlice';
import { validateToken } from './utils/tokenUtils';
import { Outlet } from 'react-router-dom';


function AppWrapper() {
  const isMobile = useMediaQuery('(max-width: 768px)');
  const location = useLocation();
  const dispatch = useDispatch();
  const { isAuthenticated } = useSelector((state) => state.auth);
  const { error: globalError } = useSelector((state) => state.ui);
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

  // Fetch current user if authenticated
  const { data: userData } = useGetCurrentUserQuery(undefined, {
    skip: !isAuthenticated,
  });

  useEffect(() => {
    if (userData) {
      dispatch(updateUser(userData));
    }
  }, [userData, dispatch]);

  // Check if current route belongs to authentication or landing page
  const isAuthPage = location.pathname === '/login' || location.pathname === '/forgot-password' || location.pathname === "/reset-password" || location.pathname === "/change-password" || location.pathname === "/offer-acceptance" || location.pathname === "/thank-you";
  const isLandingPage = location.pathname === '/';

  // Check if current route is onboarding page
  const isOnboardingPage = location.pathname.toLowerCase().startsWith('/my-team/onboarding');

  useEffect(() => {
    // Check for existing token on app load
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    if (token && validateToken(token)) {
      // Token is valid, set credentials
      store.dispatch(setCredentials({
        token: token,
        refreshToken: refreshToken,
        user: null,
      }));
    } else if (token) {
      // Token is invalid or expired, clear it
      store.dispatch(logout());
    }

    setSidebarOpen(!isMobile);
    setMobileSidebarOpen(false);
  }, [isMobile]);

  const handleSidebarToggle = () => {
    if (isMobile) {
      setMobileSidebarOpen(!mobileSidebarOpen);
    } else {
      setSidebarOpen(!sidebarOpen);
    }
  };

  const handleMobileSidebarClose = () => {
    setMobileSidebarOpen(false);
  };

  const isNoSidebarLayout = isAuthPage || isOnboardingPage || isLandingPage || !isAuthenticated;

  // Render without sidebar and navbar for certain pages (Auth, Onboarding, Landing)
  if (isNoSidebarLayout) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AppRoutes />
        <GlobalLoader />
        <ErrorModal
          isOpen={globalError.isOpen}
          onClose={() => dispatch(clearGlobalError())}
          statusCode={globalError.statusCode}
          errorMessage={globalError.errorMessage}
          errorDetails={globalError.errorDetails}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />

      {/* ROOT LAYOUT */}
      <Box sx={{ display: 'flex', height: '100vh' }}>
        {/* SIDEBAR */}
        <Sidebar
          open={sidebarOpen}
          mobileOpen={mobileSidebarOpen}
          isMobile={isMobile}
          onMobileClose={handleMobileSidebarClose}
          onSidebarOpen={() => setSidebarOpen(true)}
        />

        {/* RIGHT CONTENT */}
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
          }}
        >
          <Navbar
            sidebarOpen={sidebarOpen}
            onSidebarToggle={handleSidebarToggle}
          />

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              // p: 3,
              backgroundColor: '#fff',
              minWidth: 0,
              overflowX: 'hidden',
            }}
          >          {/* <Dashboard/> */}
            <AppRoutes />
          </Box>
        </Box>
      </Box>
      <GlobalLoader />
      <ErrorModal
        isOpen={globalError.isOpen}
        onClose={() => dispatch(clearGlobalError())}
        statusCode={globalError.statusCode}
        errorMessage={globalError.errorMessage}
        errorDetails={globalError.errorDetails}
      />
    </ThemeProvider>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppWrapper />
    </Provider>
  );
}

export default App;
